import React, { useEffect, useState } from "react";
import { X, Calendar, Users } from "lucide-react";
import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
import axios from "axios";
import SectionHeading from "../../common/SectionHeading";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

const GamesSection = ({ activeTab }) => {
  const [activeGame, setActiveGame] = useState(activeTab || null);
  const [activeSubTab, setActiveSubTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [allGames, setAllGames] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/sports-categories/all-categories`
      );
      if (data?.categories) {
        const categories = data?.categories || [];
        const excludeList = ["special attraction"];
        const filtered = categories.filter(
          (cat) => !excludeList.includes(cat.name?.toLowerCase())
        );
        setAllGames(filtered);
        setActiveGame(activeTab || filtered?.[0]?._id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    AOS.init({ duration: 800, once: false, mirror: true, easing: "ease-out" });
    fetchData();
  }, []);

  const handleGameClick = (gameId) => {
    setActiveGame(gameId);
    setActiveSubTab("overview"); // Reset to overview when switching games
  };

  return (
    <div className="w-full mx-auto px-4 md:px-8 lg:px-12">
      {loading ? (
        <GamesSkeleton />
      ) : (
        <>
          {/* Enhanced Tabs with Dropdown */}
          <div className="w-full mb-8">
            {/* Simple Clean Tabs */}
            <div
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 my-6 font-heading"
              data-aos="fade-up"
            >
              <div className="flex flex-col sm:flex-row gap-3">
                {allGames.map((game) => (
                  <button
                    key={game._id}
                    onClick={() => handleGameClick(game._id)}
                    className={`flex items-center justify-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 flex-1 group ${
                      activeGame === game._id
                        ? "bg-gradient-to-tl from-primaryBtnHoverColor via-primaryTextColor to-primaryTextColor text-white shadow-md"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg transition-colors ${
                        activeGame === game._id ? "bg-white/20" : "bg-white"
                      }`}
                      data-aos="zoom-in"
                    >
                      <img
                        src={game.iconImageUrl}
                        alt={game.title}
                        className="w-5 h-5"
                        data-aos="zoom-in"
                      />
                    </div>
                    <span className="font-semibold text-lg tracking-wide capitalize">
                      {game.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sub Navigation */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-8 font-subheading tracking-wide">
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveSubTab("overview")}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeSubTab === "overview"
                      ? "bg-gradient-to-tr from-primaryTextColor via-primaryTextColor to-primaryBtnHoverColor text-white shadow-sm"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Overview
                </button>
                <button
                  onClick={() => setActiveSubTab("programs")}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeSubTab === "programs"
                      ? "bg-gradient-to-bl from-primaryTextColor via-primaryTextColor to-primaryBtnHoverColor text-white shadow-sm"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Programs & Pricing
                </button>
              </div>

              {/* Current Sport Indicator */}
              <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-lg">
                <img
                  src={allGames.find((g) => g._id === activeGame)?.iconImageUrl}
                  alt=""
                  className="w-5 h-5"
                />
                <span className="font-medium text-sm text-gray-700 capitalize">
                  {allGames.find((g) => g._id === activeGame)?.name}
                </span>
              </div>
            </div>
          </div>

          {/* Content based on active sub-tab */}
          {activeSubTab === "overview" ? (
            <>
              <div className="">
                <ImageGrid
                  images={
                    allGames.find((g) => g._id === activeGame)?.images || []
                  }
                />
              </div>

              <div className="my-12">
                {allGames
                  .find((g) => g._id === activeGame)
                  ?.contents?.map((content, idx) => (
                    <SectionHeading
                      key={idx}
                      title={content.heading}
                      subtitle={content.description}
                      containerClassName={"py-8 md:py-8 gap-y-6"}
                      titleClassName={"lg:leading-normal "}
                      subtitleClassName={" w-full mx-auto"}
                    />
                  ))}
              </div>
            </>
          ) : (
            <ProgramsPricingSection
              game={allGames.find((g) => g._id === activeGame)}
            />
          )}
        </>
      )}
    </div>
  );
};

// Updated Programs & Pricing Component using API data
const ProgramsPricingSection = ({ game }) => {
  const navigate = useNavigate();

  // Use programs from API data, fallback to empty array
  const programs = game?.programs || [];

  const colorMap = {
    blue: {
      bg: "bg-blue-500",
      light: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-200",
    },
    red: {
      bg: "bg-red-500",
      light: "bg-red-50",
      text: "text-red-600",
      border: "border-red-200",
    },
    green: {
      bg: "bg-green-500",
      light: "bg-green-50",
      text: "text-green-600",
      border: "border-green-200",
    },
    purple: {
      bg: "bg-purple-500",
      light: "bg-purple-50",
      text: "text-purple-600",
      border: "border-purple-200",
    },
  
    // Additional Tailwind colors
    slate: {
      bg: "bg-slate-500",
      light: "bg-slate-50",
      text: "text-slate-600",
      border: "border-slate-200",
    },
    gray: {
      bg: "bg-gray-500",
      light: "bg-gray-50",
      text: "text-gray-600",
      border: "border-gray-200",
    },
    zinc: {
      bg: "bg-zinc-500",
      light: "bg-zinc-50",
      text: "text-zinc-600",
      border: "border-zinc-200",
    },
    neutral: {
      bg: "bg-neutral-500",
      light: "bg-neutral-50",
      text: "text-neutral-600",
      border: "border-neutral-200",
    },
    stone: {
      bg: "bg-stone-500",
      light: "bg-stone-50",
      text: "text-stone-600",
      border: "border-stone-200",
    },
  
    orange: {
      bg: "bg-orange-500",
      light: "bg-orange-50",
      text: "text-orange-600",
      border: "border-orange-200",
    },
    amber: {
      bg: "bg-amber-500",
      light: "bg-amber-50",
      text: "text-amber-600",
      border: "border-amber-200",
    },
    yellow: {
      bg: "bg-yellow-500",
      light: "bg-yellow-50",
      text: "text-yellow-600",
      border: "border-yellow-200",
    },
    lime: {
      bg: "bg-lime-500",
      light: "bg-lime-50",
      text: "text-lime-600",
      border: "border-lime-200",
    },
    emerald: {
      bg: "bg-emerald-500",
      light: "bg-emerald-50",
      text: "text-emerald-600",
      border: "border-emerald-200",
    },
    teal: {
      bg: "bg-teal-500",
      light: "bg-teal-50",
      text: "text-teal-600",
      border: "border-teal-200",
    },
    cyan: {
      bg: "bg-cyan-500",
      light: "bg-cyan-50",
      text: "text-cyan-600",
      border: "border-cyan-200",
    },
    sky: {
      bg: "bg-sky-500",
      light: "bg-sky-50",
      text: "text-sky-600",
      border: "border-sky-200",
    },
    indigo: {
      bg: "bg-indigo-500",
      light: "bg-indigo-50",
      text: "text-indigo-600",
      border: "border-indigo-200",
    },
    violet: {
      bg: "bg-violet-500",
      light: "bg-violet-50",
      text: "text-violet-600",
      border: "border-violet-200",
    },
    fuchsia: {
      bg: "bg-fuchsia-500",
      light: "bg-fuchsia-50",
      text: "text-fuchsia-600",
      border: "border-fuchsia-200",
    },
    pink: {
      bg: "bg-pink-500",
      light: "bg-pink-50",
      text: "text-pink-600",
      border: "border-pink-200",
    },
    rose: {
      bg: "bg-rose-500",
      light: "bg-rose-50",
      text: "text-rose-600",
      border: "border-rose-200",
    },
  };  

  // If no programs data available, show a message
  if (programs.length === 0) {
    return (
      <div className="text-center pb-8 font-subheading tracking-wide">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
          {/* <div className="text-6xl mb-4">🏀</div> */}
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Programs Coming Soon
          </h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            We're currently working on exciting programs for {game?.name}. Check
            back soon for updates on training schedules and pricing.
          </p>
          <button
            onClick={() => navigate("/contact")}
            className="bg-gradient-to-r from-primaryTextColor to-primaryBtnHoverColor text-white font-semibold py-3 px-8 rounded-xl hover:opacity-90 transition-all duration-300"
          >
            Contact Us for Details
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="font-subheading tracking-wide">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-3 bg-gradient-to-r from-gray-900 to-gray-700 text-white px-6 py-3 rounded-full text-sm font-medium mb-6">
          <span>{programs[0]?.icon || "🏀"}</span>
          <span>Professional Training Programs</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 font-heading">
          {game?.name}{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-blue-500">
            Programs
          </span>
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Transform your game with our structured training programs designed for
          <span className="font-semibold text-gray-900">
            {" "}
            every skill level
          </span>{" "}
          — from first-time players to competitive athletes.
        </p>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
        <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="text-2xl font-bold text-gray-900">
            {programs.length}
          </div>
          <div className="text-sm text-gray-500">Program Types</div>
        </div>
        <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="text-2xl font-bold text-gray-900">
            {programs.reduce(
              (total, program) => total + (program.schedule?.length || 0),
              0
            )}
            +
          </div>
          <div className="text-sm text-gray-500">Weekly Sessions</div>
        </div>
        <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="text-2xl font-bold text-gray-900">300+</div>
          <div className="text-sm text-gray-500">Active Players</div>
        </div>
        <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="text-2xl font-bold text-gray-900">5</div>
          <div className="text-sm text-gray-500">Expert Coaches</div>
        </div>
      </div>

      {/* Programs Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-20">
        {programs.map((program, index) => {
          const color = colorMap[program.color] || colorMap.blue;
          return (
            <div key={index} className="group relative">
              {/* Popular Badge */}
              {program.pricing?.some((p) => p.popular) && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="font-heading bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                    ⭐ MOST POPULAR
                  </div>
                </div>
              )}

              <div className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-500 group-hover:scale-[1.02] h-full flex flex-col">
                {/* Program Header */}
                <div
                  className={`${color.bg} p-8 text-white relative overflow-hidden`}
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-3xl">{program.icon}</span>
                      <div>
                        <h3 className="text-2xl font-bold font-heading">
                          {program.level}
                        </h3>
                        <p className="text-blue-100 font-medium">
                          {program.tagline}
                        </p>
                      </div>
                    </div>
                    <p className="text-blue-100 text-sm leading-relaxed">
                      {program.description}
                    </p>
                  </div>
                </div>

                <div className="p-8 flex-1 flex flex-col">
                  {/* Program Meta */}
                  <div className="flex gap-4 mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      {program.intensity} Intensity
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      {program.duration}
                    </div>
                  </div>

                  {/* Schedule & Pricing Side by Side */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Schedule */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full font-heading"></div>
                        Schedule
                      </h4>
                      <div className="space-y-3">
                        {program.schedule?.map((slot, idx) => (
                          <div key={idx} className="bg-gray-50 p-4 rounded-xl">
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium text-gray-900">
                                {slot.days}
                              </div>
                              <div className="font-semibold text-gray-900 text-sm bg-white px-2 py-1 rounded-md">
                                {slot.time}
                              </div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {slot.age}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pricing */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full font-heading"></div>
                        Pricing
                      </h4>
                      <div className="space-y-3">
                        {program.pricing?.map((price, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                              price.popular
                                ? `${color.border} ${color.light} border-2 shadow-md`
                                : "bg-gray-50 border-transparent"
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {price.type}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {price.sessions}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">
                                  {price.price}
                                </div>
                                {price.popular && (
                                  <div className="text-xs text-green-600 font-semibold mt-1">
                                    Best Value
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mt-auto">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full font-heading"></div>
                      What's Included
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {program.features?.map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-sm text-gray-600"
                        >
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="mt-8">
                    <button
                      onClick={() => {
                        if (program.button?.text === "Contact Us") {
                          navigate("/contact");
                          window.scrollTo({top:0, behavior : "smooth"})
                        } else if (
                          program.button?.text === "Register Now" &&
                          program.button?.url
                        ) {
                          window.open(
                            program.button.url,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      }}
                      className={`w-full ${color.bg} hover:opacity-90 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg group/btn relative overflow-hidden`}
                    >
                      <div className="relative z-10 flex items-center justify-center gap-2">
                        <span>{program.button?.text || "Contact Us"}</span>

                        <span className="group-hover/btn:translate-x-1 transition-transform">
                          {program.button?.text === "Register Now" ? "📝" : "💬"}
                        </span>
                      </div>

                      <div className="absolute inset-0 bg-white/20 transform -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
                    </button>
                  </div>



                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Rest of the existing components (GamesSkeleton, ImageGrid) remain the same
const GamesSkeleton = () => {
  return (
    <div className="w-full animate-pulse">
      <div className="text-center my-6 sm:my-8 md:my-10">
        <div className="w-48 h-8 bg-gray-300 mx-auto rounded mb-3"></div>
        <div className="w-72 h-4 bg-gray-300 mx-auto rounded"></div>
      </div>

      <div className="flex w-full gap-2 justify-between my-5 sm:my-6 md:my-7 lg:my-8">
        {Array.from({ length: 5 }).map((_, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 px-2 py-2 rounded-md"
          >
            <div className="w-7 h-7 bg-gray-300 rounded-full"></div>
            <div className="hidden sm:block w-20 h-4 bg-gray-300 rounded"></div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {Array.from({ length: 2 }).map((_, idx) => (
          <div
            key={idx}
            className="w-full aspect-[5/3] bg-gray-300 rounded-lg"
          ></div>
        ))}
      </div>

      <div className="my-12 space-y-10">
        {Array.from({ length: 2 }).map((_, idx) => (
          <div key={idx} className="text-center space-y-3">
            <div className="w-56 h-6 bg-gray-300 mx-auto rounded"></div>
            <div className="w-96 h-4 bg-gray-300 mx-auto rounded"></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ImageGrid = ({ images = [] }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  useBodyScrollLock(selectedImage);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && selectedImage) {
        closeLightbox();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [selectedImage]);

  const openLightbox = (facility) => {
    setSelectedImage(facility);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  if (!images.length) {
    return <p className="text-center text-gray-500">No images available</p>;
  }

  return (
    <div className="w-full">
      <div className="w-full mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 md:gap-6">
          {images.map((img, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 bg-black"
            >
              <div
                onClick={() => openLightbox(img)}
                className="relative aspect-[5/3] overflow-hidden"
              >
                <img
                  src={img}
                  alt={""}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-5xl w-full">
            <div className="absolute hidden sm:block top-4 left-4 z-10 bg-black/50 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full">
              <span className="text-white text-xs sm:text-sm font-medium">
                Press ESC to close
              </span>
            </div>
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative aspect-[16/10] rounded-2xl overflow-hidden">
              <img
                src={selectedImage}
                alt={""}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamesSection;
